import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { ReactComponent as ChevronRightSvg } from "@/src/assets/icons/icon-chevron-right.svg";
import InfoSvg from "@/src/assets/icons/icon-Info-blue4-fill.svg";
import MailSvg from "@/src/assets/icons/icon-mail.svg";
import BasicButton from "@/src/components/atom/Button/BasicButton";
import Typo from "@/src/components/atom/Typo";
import Icon from "@/src/components/atom/Icon";
import CodeInput from "@/src/components/atom/Input/CodeInput";
import { InputError } from "@/src/components/molecule/FormItem";
import CallOut from "@/src/components/molecule/CallOut";
import useAlert from "@/src/hooks/useAlert";
import PUBLIC_PATH from "@/src/routes/public/path";
import colorSet from "@/src/styles/color";
import { useLazyGetExporterQuery, useSignOutMutation, useRequestCompanyCodeMutation } from "@/src/store/apis/auth";
import { persistor } from "@/src/store";
import {
  MemberSignupStepContext,
  MemberSignUpResourceContext,
} from "./index";

interface LocationState {
  companyName: string;
  email: string;
  name: string;
}

const MemberSignupStepOne: React.FC = () => {
  const { t } = useTranslation(["signup", "common", "error"]);
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const alert = useAlert();

  const stepContext = useContext(MemberSignupStepContext);
  const resourceContext = useContext(MemberSignUpResourceContext);

  const [corpCode, setCorpCode] = useState("");
  const [error, setError] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingRequest, setIsSendingRequest] = useState(false);

  const [getExporter] = useLazyGetExporterQuery();
  const [signOut] = useSignOutMutation();
  const [requestCompanyCode] = useRequestCompanyCodeMutation();

  const onResourceChange = resourceContext?.onResourceChange;

  useEffect(() => {
    if (!locationState?.companyName || !locationState?.email || !locationState?.name) {
      alert.showAlert({
        message: t("signup:invalidAccess"),
        type: "error",
      });
      navigate(PUBLIC_PATH.SIGNUP, { replace: true });
      return;
    }

    onResourceChange?.("companyName", locationState.companyName);
    onResourceChange?.("email", locationState.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.companyName, locationState?.email, locationState?.name]);

  if (!stepContext || !resourceContext) {
    return null;
  }

  if (!locationState?.companyName || !locationState?.email || !locationState?.name) {
    return null;
  }

  const { companyName } = locationState;
  const { onStepNext } = stepContext;

  const handleBack = async () => {
    try {
      await signOut().unwrap();
      await persistor.purge();
      window.location.href = PUBLIC_PATH.LOGIN;
    } catch (error) {
      console.error("Logout failed:", error);
      window.location.href = PUBLIC_PATH.LOGIN;
    }
  };

  const handleNext = async () => {
    if (corpCode.length !== 8) {
      setError(t("error:required"));
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      const result = await getExporter({
        companyCode: corpCode,
      }).unwrap();

      if (!result || result.length === 0) {
        setError(t("error:invalidCorpCode"));
        return;
      }

      const exporter = result[0];

      if (exporter.companyName !== resourceContext.companyName) {
        setError(t("error:companyMismatchError"));
        return;
      }

      resourceContext.onResourceChange("corpCode", corpCode);
      resourceContext.onResourceChange("exporterId", exporter.id);
      resourceContext.onResourceChange(
        "mainCategoryCodeItemNames",
        exporter.mainCategoryCodeItemNames
      );
      resourceContext.onResourceChange(
        "autoApproveNewMembers",
        exporter.autoApproveNewMembers
      );

      onStepNext();
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };
      const errorMessage = err.data?.message || t("error:invalidCorpCode");
      setError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendMailRequest = async () => {
    if (!locationState?.companyName || !locationState?.email || !locationState?.name) {
      return;
    }

    if (isSendingRequest) {
      return;
    }

    setIsSendingRequest(true);
    try {
      await requestCompanyCode({
        companyName: locationState.companyName,
        employeeEmail: locationState.email,
        employeeName: locationState.name,
      }).unwrap();

      alert.showAlert({
        message: t("signup:v2.pushRequestSent"),
        type: "success",
      });
    } catch (error) {
      alert.showAlert({
        message: t("error:somethingWentWrong"),
        type: "error",
      });
    } finally {
      setIsSendingRequest(false);
    }
  };

  const handleCodeChange = (value: string) => {
    setCorpCode(value);
    if (error) {
      setError("");
    }
  };

  return (
    <Container>
      <TitleWrapper>
        <Subtitle>
          <Typo typoType="d6" color="gray2">
            {t("signup:v2.foundWorkspace", { companyName })}
          </Typo>
        </Subtitle>
        <Description>
          <Typo typoType="b6r" color="gray4">
            {t("signup:v2.enterInvitationCode")}
          </Typo>
        </Description>
      </TitleWrapper>

      <FormContainer>
        <CallOut
          icon={<Icon iconSrc={InfoSvg} />}
          value={
            <CalloutContent>
              <Typo typoType="b9m" color="indigo">
                {t("signup:v2.requestCodeInfo")}
              </Typo>
              <CalloutLink onClick={handleSendMailRequest}>
                <BlueText typoType="b9m" color="systemBlue2">
                  {t("signup:v2.sendMailRequest")}
                </BlueText>
                <IconContainer>
                  <ChevronRightIcon />
                </IconContainer>
              </CalloutLink>
            </CalloutContent>
          }
        />

        <MailIconContainer>
          <Icon iconSrc={MailSvg} iconSize={80} />
        </MailIconContainer>

        <InstructionsContainer>
          <Typo typoType="d6" color="gray1">
            {t("signup:v2.enterCorporateCodeTitle")}
          </Typo>
          <Typo typoType="b7m" color="gray2">
            {t("signup:v2.enterCorporateCodeDesc")}
          </Typo>
        </InstructionsContainer>

        <FormField>
          <Label>
            <Typo typoType="b7m" color="gray2">
              {t("common:corporateCode")}
            </Typo>
            <Required>*</Required>
          </Label>
          <CodeInput
            length={8}
            value={corpCode}
            onChange={handleCodeChange}
            error={!!error}
            disabled={isVerifying}
          />
          {error && <InputError message={error} />}
        </FormField>

        <ButtonGroup>
          <BasicButton
            buttonGrade="tertiary"
            buttonColor="black"
            buttonSize={40}
            onClick={handleBack}
            disabled={isVerifying}
            style={{
              width: "236px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typo typoType="btn3m" color="gray2">
              {t("common:back")}
            </Typo>
          </BasicButton>
          <BasicButton
            buttonGrade="primary"
            buttonColor="blue"
            buttonSize={40}
            onClick={handleNext}
            disabled={corpCode.length !== 8 || isVerifying}
            style={{
              width: "236px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typo typoType="btn3m" color="white">
              {t("common:next")}
            </Typo>
          </BasicButton>
        </ButtonGroup>
      </FormContainer>
    </Container>
  );
};

export default MemberSignupStepOne;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
  max-width: 1280px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Subtitle = styled.div`
  text-align: center;
  width: 100%;
  padding: 8px 0;

  span {
    color: ${colorSet.blue4};
  }
`;

const Description = styled.div`
  text-align: center;
  width: 100%;
  padding: 8px 0;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  width: 100%;
  max-width: 720px;
  padding: 40px 48px;
  background: ${colorSet.white};
  border-radius: 16px;
  box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.1),
    0px 4px 16px 0px rgba(0, 0, 0, 0.1);
`;

const CalloutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CalloutLink = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const BlueText = styled(Typo)`
  text-decoration-line: underline;
  text-underline-position: under;
  text-decoration-thickness: 1px;
`;

const ChevronRightIcon = styled(ChevronRightSvg)`
  width: 16px;
  height: 16px;

  path {
    fill: ${colorSet.systemBlue2};
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MailIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
`;

const InstructionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  width: 100%;
  max-width: 544px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 460px;  
  align-items: center;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 2px;
  width: 100%;
`;

const Required = styled.span`
  color: ${colorSet.red2};
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 480px;
`;
